import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Edit2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ cost_cents: 0, cache_duration_hours: 24 });

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/services");
      setServices(data);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (svc) => {
    await api.put(`/admin/services/${svc.service_id}`, { active: !svc.active });
    toast.success(`Servizio ${!svc.active ? "attivato" : "disattivato"}`);
    load();
  };

  const openEdit = (svc) => {
    setEditing(svc);
    setForm({ cost_cents: svc.cost_cents, cache_duration_hours: svc.cache_duration_hours });
  };

  const save = async () => {
    await api.put(`/admin/services/${editing.service_id}`, {
      cost_cents: parseInt(form.cost_cents),
      cache_duration_hours: parseInt(form.cache_duration_hours),
    });
    toast.success("Servizio aggiornato");
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl tracking-tight font-medium text-text-primary">Catalogo servizi</h1>
        <p className="text-sm text-text-secondary mt-1">Gestione costi, cache e disponibilità.</p>
      </div>

      <Card className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
        <Table data-testid="admin-services-table">
          <TableHeader>
            <TableRow className="bg-subtle hover:bg-subtle">
              <TableHead>Servizio</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-right">Costo (€)</TableHead>
              <TableHead className="text-right">Cache (ore)</TableHead>
              <TableHead>Attivo</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-text-muted py-8">Caricamento…</TableCell></TableRow>
            ) : failed ? (
              <TableRow><TableCell colSpan={6}><LoadErrorBox what="i servizi" onRetry={load} testId="admin-services-error" /></TableCell></TableRow>
            ) : services.map((s) => (
              <TableRow key={s.service_id} data-testid={`svc-row-${s.service_id}`}>
                <TableCell className="text-sm font-medium text-text-primary">{s.name}</TableCell>
                <TableCell className="text-sm text-text-secondary">{s.category}</TableCell>
                <TableCell className="text-right text-sm">{((s.cost_cents || 0) / 100).toFixed(2)}</TableCell>
                <TableCell className="text-right text-sm">{s.cache_duration_hours}</TableCell>
                <TableCell>
                  <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`svc-active-${s.service_id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`svc-edit-${s.service_id}`} className="h-8 px-2">
                    <Edit2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader><DialogTitle>Modifica - {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-text-secondary">Costo (centesimi)</Label>
              <Input type="number" value={form.cost_cents} onChange={(e) => setForm({ ...form, cost_cents: e.target.value })}
                data-testid="svc-edit-cost" className="h-11 rounded-lg border-border" />
              <div className="text-xs text-text-muted">= € {((parseInt(form.cost_cents) || 0) / 100).toFixed(2)}</div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-text-secondary">Durata cache (ore)</Label>
              <Input type="number" value={form.cache_duration_hours} onChange={(e) => setForm({ ...form, cache_duration_hours: e.target.value })}
                data-testid="svc-edit-cache" className="h-11 rounded-lg border-border" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-lg">Annulla</Button>
              <Button onClick={save} data-testid="svc-edit-save" className="rounded-lg bg-accent hover:bg-accent-strong text-white">Salva</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
